import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import {
  CheckCircledIcon,
  CircleIcon,
  CrossCircledIcon,
  OpenInNewWindowIcon,
  StopwatchIcon,
  FileTextIcon,
  ClipboardCopyIcon,
  MagicWandIcon,
} from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import React from "react";

export enum PlyExportationStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  OPTIMIZING = "optimizing",
  SUCCESS = "success",
  FAILED = "failed",
}

const statuses = {
  [PlyExportationStatus.PENDING]: {
    label: "Pending",
    icon: CircleIcon,
    color: "text-muted-foreground",
  },
  [PlyExportationStatus.PROCESSING]: {
    label: "Processing",
    icon: StopwatchIcon,
    color: "text-muted-foreground",
  },
  [PlyExportationStatus.OPTIMIZING]: {
    label: "Optimizing",
    icon: MagicWandIcon,
    color: "text-blue-500",
  },
  [PlyExportationStatus.SUCCESS]: {
    label: "Success",
    icon: CheckCircledIcon,
    color: "text-green-500",
  },
  [PlyExportationStatus.FAILED]: {
    label: "Failed",
    icon: CrossCircledIcon,
    color: "text-red-500",
  },
};

export interface PlyExportation {
  _id: string;
  mobileOrUid: string;
  planNo: number;
  actualPlanNo?: number;
  status: PlyExportationStatus;
  createdAt: string;
  reportLink: string;
}

export const columns: ColumnDef<PlyExportation>[] = [
  {
    accessorKey: "createdAt",
    header: "Date",
    cell: (props) => {
      return new Date(`${props.getValue()}`).toLocaleString();
    },
  },
  {
    accessorKey: "mobileOrUid",
    header: "Mobile Or UID",
  },
  {
    accessorKey: "actualPlanNo",
    header: "Plan No",
    cell: (props) => {
      const value = props.getValue();
      if (value === undefined || value === null) return "-";
      return `${value}`;
    },
  },
  {
    accessorKey: "planNo",
    header: "Version Number",
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const statusValue = row.getValue("status") as string;
      const status = statuses[statusValue];
      if (!status) return null;

      return (
        <div className="flex w-[100px] items-center">
          {status.icon && (
            <status.icon className={cn(["mr-2 h-4 w-4", status.color])} />
          )}
          <span
            className={cn(
              statusValue === PlyExportationStatus.PENDING &&
                "text-muted-foreground"
            )}
          >
            {status.label}
          </span>
        </div>
      );
    },
  },
  {
    header: "View",
    cell: ({ row }) => {
      const { toast } = useToast();

      const viewerLink = `${window.location.origin}/dental-viewer/revamped-view.html?id=${row.original._id}`;

      const copyLink = () => {
        navigator?.clipboard?.writeText(viewerLink);
        toast({ title: "Viewer Link Copied!" });
      };

      if (row.original.status === "success")
        return (
          <div className="flex items-center gap-1">
            <a
              href={viewerLink}
              target="_blank"
            >
              <OpenInNewWindowIcon className="text-gray-500" />
            </a>
            <Button onClick={copyLink} variant="ghost" size="sm">
              <ClipboardCopyIcon className="text-gray-500" />
            </Button>
          </div>
        );
    },
  },
  {
    header: "PDF Report",
    cell: ({ row }) => {
      const { toast } = useToast();

      const reportLink = row.original.reportLink;
      const downloadFile = async () => {
        if (reportLink) {
          const link = document.createElement("a");
          link.download = "Report PDF";

          link.href = reportLink;
          link.target = "window";

          link.click();
        }
      };

      const copyLink = () => {
        navigator?.clipboard?.writeText(reportLink);
        toast({ title: "Report Link Copied!" });
      };

      if (row.original.status === "success" && reportLink)
        return (
          <>
            <Button onClick={downloadFile} variant="ghost" size="sm">
              <FileTextIcon className="text-gray-500 mr-2" /> View
            </Button>
            <Button onClick={copyLink} variant="ghost" size="sm">
              <ClipboardCopyIcon className="text-gray-500 mr-2" /> Copy Link
            </Button>
          </>
        );
    },
  },
];
